/**
 * Панель настройки страницы-списка (АН-09): выбор сохранённого вида, «Настроить» (колонки, отбор, группировка,
 * сортировка, итоги, вид), «Сохранить вид» личным или общим, «Сбросить». Выбор запоминается за человеком.
 */
import { Save, SlidersHorizontal, Trash2, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select } from '../../ui/form';
import type { ListViewState } from './useListView';
import { configSummary } from './util';
import { ViewEditor } from './ViewEditor';

function SaveDialog({ lv, open, onClose }: { lv: ListViewState; open: boolean; onClose: () => void }) {
  const mine = lv.current?.mine ? lv.current : null;
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const busy = lv.create.isPending || lv.update.isPending;
  const err = (lv.create.error ?? lv.update.error) as Error | null;
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title="Сохранить вид"
      description="Вид запоминает колонки, отбор, группировку, сортировку и итоги."
      width="sm"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button onClick={onClose}>Закрыть</Button>
          {mine ? (
            <Button
              variant="secondary"
              loading={lv.update.isPending}
              onClick={() =>
                lv.update.mutate(
                  { id: mine.id, withConfig: true },
                  {
                    onSuccess: () => {
                      toast(`Вид «${mine.name}» обновлён`);
                      onClose();
                    },
                  },
                )
              }
            >
              Обновить «{mine.name}»
            </Button>
          ) : null}
          <Button
            variant="primary"
            loading={busy && !mine}
            disabled={!name.trim()}
            onClick={() =>
              lv.create.mutate(
                { name: name.trim(), shared },
                {
                  onSuccess: () => {
                    toast('Вид сохранён');
                    setName('');
                    onClose();
                  },
                },
              )
            }
          >
            Сохранить как новый
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Название вида">
          <Input
            className="h-11 md:h-10"
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            placeholder="Например, «Просроченные»"
          />
        </Field>
        <Checkbox
          className="min-h-11 items-center"
          label="Общий вид"
          description="Увидят все, у кого есть право на данные этого списка. Менять его сможете только вы."
          checked={shared}
          onChange={(e) => setShared(e.target.checked)}
        />
        {err ? <p className="text-[13px] text-danger">{err.message}</p> : null}
      </div>
    </Dialog>
  );
}

export function ListViewBar({ lv, actions }: { lv: ListViewState; actions?: React.ReactNode }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const { src, config } = lv;
  if (!src || !config) return null;
  const summary = configSummary(lv.cleaned ?? config);
  return (
    <div className="flex flex-col gap-3 border-b border-line px-4 py-3">
      <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center">
        <Select
          className="h-11 md:h-10 md:w-72"
          aria-label="Сохранённый вид"
          value={lv.viewId ?? ''}
          onChange={(e) => lv.selectView(e.target.value || null)}
        >
          <option value="">Стандартный вид</option>
          {lv.views.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
              {v.shared ? (v.mine ? ' · общий' : ` · общий, ${v.ownerName ?? 'коллега'}`) : ''}
            </option>
          ))}
        </Select>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" icon={<SlidersHorizontal className="size-4" />} onClick={() => setEditing(true)}>
            Настроить
          </Button>
          <Button size="sm" icon={<Save className="size-4" />} onClick={() => setSaving(true)}>
            Сохранить вид
          </Button>
          <Button
            size="sm"
            variant="ghost"
            icon={<Undo2 className="size-4" />}
            onClick={lv.reset}
            disabled={!lv.dirty && !lv.viewId}
          >
            Сбросить
          </Button>
          {lv.current?.mine ? (
            <Button
              size="sm"
              variant="ghost"
              icon={<Trash2 className="size-4" />}
              onClick={() => {
                const v = lv.current;
                if (v && window.confirm(`Удалить вид «${v.name}»?`))
                  lv.remove.mutate(v.id, { onSuccess: () => toast('Вид удалён') });
              }}
            >
              Удалить вид
            </Button>
          ) : null}
          {actions}
        </div>
      </div>
      <p className="text-[12px] text-ink-2">
        {lv.dirty ? 'Вид изменён и пока не сохранён. ' : ''}
        {summary || 'Без отбора и группировки.'} Выбор запоминается за вами.
      </p>
      <Dialog
        open={editing}
        onOpenChange={setEditing}
        title="Настройка списка"
        description={src.title}
        width="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="primary" onClick={() => setEditing(false)}>
              Готово
            </Button>
          </div>
        }
      >
        <ViewEditor src={src} config={config} onChange={lv.setConfig} />
      </Dialog>
      <SaveDialog lv={lv} open={saving} onClose={() => setSaving(false)} />
    </div>
  );
}
